import { BookedVisitStatusType, VisitVariantType } from '@csp/csp-common-model';
import { WebHcpPatientVisitEntry } from '@csp/csp-web-content-definitions';
import { BookedVisit } from '../model/BookedVisit';
import { PatientVisitTexts } from '../model/PatientVisitTexts';
import { ScheduleVisitTexts } from '../model/ScheduleVisitTexts';
import { ScheduleVisitsContent } from '../model/ScheduleVisitsContent';
import { GeneratedVisit } from '../schedule/model/GeneratedVisit';
import { VisitRequest } from '../schedule/model/VisitRequest';
import { ScheduleVisitTextsMapper } from './ScheduleVisitTextsMapper';

const from = (
  status: BookedVisitStatusType | 'NOT_BOOKED',
  patientVisitTexts: WebHcpPatientVisitEntry,
  scheduleCms: ScheduleVisitTexts,
  request: VisitRequest,
): PatientVisitTexts => ({
  status: {
    label: patientVisitTexts.visitDetails.textLabel.status.label,
    value: patientVisitTexts.visitStatus.enum[status],
  },
  visitKind: {
    label: patientVisitTexts.visitDetails.textLabel.visitType.label,
    value: scheduleCms.visitKind ?? request?.details.visitKind,
  },
  visitName: {
    label: patientVisitTexts.visitDetails.textLabel.visitTitle.label,
    value: scheduleCms.title ?? request.requestCode,
  },
  visitPurpose: scheduleCms.hcpDescriptionMd
    ? {
        label: patientVisitTexts.visitDetails.textLabel.visitPurpose.label,
        value: scheduleCms.hcpDescriptionMd,
      }
    : undefined,
  patientDescription: scheduleCms.patientDescriptionMd
    ? {
        label: patientVisitTexts.visitDetails.textLabel.shownPatientApp.label,
        value: scheduleCms.patientDescriptionMd,
      }
    : undefined,
  patientPreparations: scheduleCms.preActivitiesMd
    ? {
        label: patientVisitTexts.visitDetails.textLabel.patientPreparations.label,
        value: scheduleCms.preActivitiesMd,
      }
    : undefined,
  visitConfirmationLearnMore: {
    labelsAndDescriptions: scheduleCms.visitConfirmationLearnMore?.map(learnMore => ({
      label: learnMore.header_and_body?.header,
      bodyMd: learnMore.header_and_body?.body_text_md,
    })),
    title: patientVisitTexts.visitConfirmationLearnMore,
  },
  bioSampleKitDescriptions: scheduleCms.bioSampleKitDescriptions,
  bioSampleAnalyteTypes: scheduleCms.bioSampleAnalyteTypes,
  bioSampleSampleTypes: scheduleCms.bioSampleSampleTypes,
  bioSampleProcessedSampleTypes: scheduleCms.bioSampleProcessedSampleTypes,
  visitAssociatedTasks: patientVisitTexts.associatedTasksCard,
});

const fromBookedVisit = (
  visit: BookedVisit,
  status: BookedVisitStatusType,
  patientVisitTexts: WebHcpPatientVisitEntry,
  visitsContent: ScheduleVisitsContent,
  visitRequest: VisitRequest,
): PatientVisitTexts => {
  const scheduleCms: ScheduleVisitTexts = ScheduleVisitTextsMapper.fromVisitRequest(
    patientVisitTexts,
    visitsContent,
    visitRequest,
    visit,
  );
  return from(status, patientVisitTexts, scheduleCms, visitRequest);
};

const fromGeneratedVisit = (
  generatedVisit: GeneratedVisit,
  patientVisitTexts: WebHcpPatientVisitEntry,
  visitsContent: ScheduleVisitsContent,
): PatientVisitTexts => {
  const scheduleCms: ScheduleVisitTexts =
    generatedVisit.request?.variant === VisitVariantType.UNSCHEDULED
      ? {
          title: patientVisitTexts.visitDetails.textLabel.unscheduledVisit.label,
          visitKind: patientVisitTexts.visitType.enum.UNSCHEDULED,
        }
      : visitsContent.getScheduleVisitTexts(generatedVisit.requestRef);
  return from('NOT_BOOKED', patientVisitTexts, scheduleCms, generatedVisit.request);
};

export const PatientVisitTextsMapper = {
  fromBookedVisit,
  fromGeneratedVisit,
};
